// Vordefinierte Piktogramm-Quellen für Urheberrechts-/Quellenangaben.
// Neue Quellen werden über die Quellenverwaltungsseite hinzugefügt,
// indem man bestehende Piktogramme einer neuen Quelle zuweist.

import { METACOM_LICENSE_TEXT, GENERAL_COPYRIGHT_TEXT } from './licenseTexts';

export const DEFAULT_SOURCE = 'eigen';

/** Quellen, die nicht gelöscht werden dürfen */
export const PROTECTED_SOURCES = new Set(['metacom', 'eigen', 'sb-piktogramme', 'sb-gebaerdenkarten']);

export type StorageMode = 'server' | 'local' | 'user-choice';

export interface PictogramSource {
  value: string;
  label: string;
  defaultAttribution: string;
  defaultLicense: string;
  storageMode: StorageMode;
  licenseAgreement?: string;
  importEnabled: boolean;
  allowKiGeneration: boolean;
  maxLocalImages?: number;
  isProtected: boolean;
  requiresLicense: boolean;
  /** Quelle wird nur sichtbar wenn der User sie importiert/bestätigt hat */
  requiresImportForVisibility?: boolean;
}

/** Default-Quellen für neue Benutzer (SB-eigene + Benutzer Bilder) */
export const DEFAULT_VISIBLE_SOURCES = ['sb-piktogramme', 'sb-gebaerdenkarten', 'eigen'];

/** SB-eigene Quellen sind immer öffentlich. */
export const ALWAYS_PUBLIC_SOURCE_VALUES = new Set(['sb-piktogramme', 'sb-gebaerdenkarten']);

/** 3rd-Party-Quellen, die erst nach Import sichtbar werden */
export const THIRD_PARTY_SOURCES = ['metacom'];

export const SOURCES: PictogramSource[] = [
  {
    value: 'sb-piktogramme',
    label: 'SB-Piktogramme',
    defaultAttribution: 'symbolbuch.de',
    defaultLicense: 'symbolbuch.de Piktogramme',
    storageMode: 'server',
    importEnabled: false,
    allowKiGeneration: false,
    isProtected: true,
    requiresLicense: false,
  },
  {
    value: 'sb-gebaerdenkarten',
    label: 'SB-Gebärdenkarten',
    defaultAttribution: 'Gebärdenkarten (DGS) – symbolbuch.de',
    defaultLicense: 'symbolbuch.de Gebärdenkarten',
    storageMode: 'server',
    importEnabled: false,
    allowKiGeneration: false,
    isProtected: true,
    requiresLicense: false,
  },
  {
    value: 'eigen',
    label: 'Benutzer Bilder',
    defaultAttribution: '',
    defaultLicense: 'Eigennutzung',
    storageMode: 'server',
    licenseAgreement: GENERAL_COPYRIGHT_TEXT,
    importEnabled: true,
    allowKiGeneration: true,
    isProtected: true,
    requiresLicense: false,
  },
  {
    value: 'metacom',
    label: 'METACOM',
    defaultAttribution: '© METACOM Symbole – www.metacom-symbole.de',
    defaultLicense: 'METACOM Lizenz',
    storageMode: 'local',
    licenseAgreement: METACOM_LICENSE_TEXT,
    importEnabled: true,
    allowKiGeneration: false,
    maxLocalImages: 500,
    isProtected: true,
    requiresLicense: true,
    requiresImportForVisibility: true,
  },
];

// Legacy source mapping for backward compatibility with existing data
export const LEGACY_SOURCE_MAP: Record<string, string> = {
  'ki-generiert': 'sb-piktogramme',
  'gebaerdenkarten': 'sb-gebaerdenkarten',
  'internet': 'eigen',
  'sonstiges': 'eigen',
};

/** Alle Quellen-Werte als Array */
export const SOURCE_VALUES = SOURCES.map((s) => s.value);

/** Sortier-Index für Quellen (Reihenfolge wie in SOURCES definiert) */
export const sourceOrderIndex = (value: string): number => {
  const mapped = LEGACY_SOURCE_MAP[value] ?? value;
  const idx = SOURCES.findIndex((s) => s.value === mapped);
  return idx >= 0 ? idx : SOURCES.length;
};

export const isAlwaysPublicSource = (value: string) => {
  const mapped = LEGACY_SOURCE_MAP[value] ?? value;
  return ALWAYS_PUBLIC_SOURCE_VALUES.has(mapped);
};

/** Label für einen Quellen-Wert (unterstützt Legacy-Werte) */
export const sourceLabel = (value: string) => {
  const mapped = LEGACY_SOURCE_MAP[value] ?? value;
  return SOURCES.find((s) => s.value === mapped)?.label ?? value;
};

/** Defaults (Attribution + Lizenz) für eine Quelle (unterstützt Legacy-Werte) */
export const getSourceDefaults = (value: string) => {
  const mapped = LEGACY_SOURCE_MAP[value] ?? value;
  const source = SOURCES.find((s) => s.value === mapped);
  return {
    attribution: source?.defaultAttribution ?? '',
    license: source?.defaultLicense ?? '',
  };
};

/** Map legacy source value to current value */
export const resolveSource = (value: string): string =>
  LEGACY_SOURCE_MAP[value] ?? value;
